if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.updateVerticalMode();

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', () => this.setActiveMedia(mediaToSwitch.dataset.target, false));
      });
      this.mql.addEventListener('change', () => this.updateVerticalMode());
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    /**
     * @param {string} mediaId
     * @param {boolean} prepend - Move media to front of gallery
     * @param {boolean} skipReset - Skip slider reset/scroll; use when caller will reset after batch DOM operations
     */
    setActiveMedia(mediaId, prepend, skipReset = false) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider && !skipReset) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      if (!skipReset) {
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({behavior: 'smooth'});
          }
        });
      }
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.isThumbnailFullyVisible(thumbnail)) return;

      if (this.isVerticalThumbnails) {
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }
    }

    isThumbnailFullyVisible(thumbnail) {
      const container = this.elements.thumbnails.slider;
      const containerRect = container.getBoundingClientRect();
      const thumbRect = thumbnail.getBoundingClientRect();
      const margin = (this.isVerticalThumbnails ? thumbRect.height : thumbRect.width) * 0.5;

      return this.isVerticalThumbnails
        ? (thumbRect.top >= containerRect.top + margin && thumbRect.bottom <= containerRect.bottom - margin)
        : (thumbRect.left >= containerRect.left + margin && thumbRect.right <= containerRect.right - margin);
    }

    updateVerticalMode() {
      this.isVerticalThumbnails = this.elements.thumbnails.slider &&
        getComputedStyle(this.elements.thumbnails.slider).flexDirection === 'column';
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    filterMediaByVariant(variantId, sectionId) {
      const variantIdStr = variantId ? variantId.toString() : '';

      // Helper to check if variant matches (supports comma-separated IDs)
      const variantMatches = (itemVariantId) => {
        if (!itemVariantId) return true; // Generic images always show
        return itemVariantId.split(',').includes(variantIdStr);
      };

      // Filter main gallery items
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
        const shouldShow = variantMatches(item.dataset.variantId);
        item.classList.toggle('hidden', !shouldShow);
      });

      // Filter thumbnails
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
          const shouldShow = variantMatches(thumb.dataset.variantId);
          thumb.classList.toggle('hidden', !shouldShow);
        });
      }

      // Filter modal images
      if (sectionId) {
        const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
        if (modalContent) {
          modalContent.querySelectorAll('[data-media-id]').forEach((item) => {
            const shouldShow = variantMatches(item.dataset.variantId);
            item.classList.toggle('hidden', !shouldShow);
          });
        }
      }

      // Note: resetPages() is called by the caller after all DOM operations complete
    }
  });
}
